/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
// @mui icons
import Icon from "@mui/material/Icon";
import Logout from "pages/logout";
// Horizon Scan page (tabbed). The V2 dashboard swaps in for the Dashboard TAB only, inside this page,
// when tenant.modules.horizonV2 is on — the Emerging Themes and Search Parameters tabs are unchanged.
import AnticipateHorizonScan from "pages/Anticipate/AnticipateHorizonScan";
import AnticipateEmergingRisk from "pages/Anticipate/AnticipateEmergingRisk";
import AnticipateDashboard from "pages/Anticipate/AnticipateDashboard";
import AnticipateHome from "pages/Anticipate/AnticipateHome";
import BoardPaper from "pages/Anticipate/BoardPaper";
import RiskAdvisor from "pages/Anticipate/RiskAdvisor";
import ProtectDashboard from "pages/ProtectDashboard/ProtectDashboard";

import Profile from "pages/Profile";
import { ReactComponent as HomeIcon } from "./assets/images/icons/projectIcons/home_black_24dp.svg";
import { ReactComponent as ProtectIcon } from "./assets/images/icons/projectIcons/speed_black_24dp.svg";
import { ReactComponent as AnticipateIcon } from "./assets/images/icons/projectIcons/pie_chart_outline_black_24dp.svg";
import { ReactComponent as ConnectIcon } from "./assets/images/icons/projectIcons/hub_black_24dp.svg";
import { ReactComponent as CheckIcon } from "./assets/images/icons/projectIcons/health_and_safety_black_24dp.svg";

function getRoutes(modules) {
  return [
    {
      type: "collapse",
      name: "Home",
      key: "dashboard",
      icon: <HomeIcon />,
      route: "/home",
      noCollapse: true,
      // New AnticipateHome (v15 snapshot) is opt-in per tenant via modules.newHomeDashboard;
      // everyone else keeps the classic dashboard until the flag is flipped in the tenant DB.
      component: modules?.newHomeDashboard ? <AnticipateHome /> : <AnticipateDashboard />,
    },
    // Always-available preview of the new home while it is gated off for the tenant (not in nav).
    {
      type: "route",
      key: "dashboard-new-preview",
      route: "/home-new",
      component: <AnticipateHome />,
    },
    // Fallback to the previous dashboard while the new home is validated (not shown in the nav).
    {
      type: "route",
      key: "dashboard-classic",
      route: "/home-classic",
      component: <AnticipateDashboard />,
    },
    // { type: "divider", key: "divider-1" },
    ...(modules?.showProtect
      ? [
          {
            type: "collapse",
            name: "Protect",
            key: "protect",
            route: "/overview",
            icon: <ProtectIcon />,
            noCollapse: true,
            component: <ProtectDashboard />,
          },
        ]
      : []),
    // { type: "divider", key: "divider-2" },
    ...(modules?.showAnticipate
      ? [
          {
            type: "collapse",
            name: "Anticipate",
            key: "anticipate",
            route: "/dashboard",
            icon: <AnticipateIcon />,
            collapse: [
              {
                name: "Horizon Scan",
                key: "horizonScan",
                route: "/anticipate/HorizonScan",
                icon: <Icon fontSize="small">radar</Icon>,
                component: <AnticipateHorizonScan />,
              },
              {
                name: "Watchlist Risks",
                key: "emergingRisks",
                route: "/anticipate/EmergingRisks",
                icon: <Icon fontSize="small">visibility</Icon>,
                component: <AnticipateEmergingRisk />,
              },
              {
                name: "Board Paper",
                key: "boardPaper",
                route: "/anticipate/board-paper",
                icon: <Icon fontSize="small">description</Icon>,
                component: <BoardPaper />,
              },
            ],
          },
        ]
      : []),
    // { type: "divider", key: "divider-3" }
    ...(modules?.showConnect
      ? [
          {
            type: "collapse",
            name: "Connect",
            key: "connect",
            route: "/dashboard",
            icon: <ConnectIcon />,
          },
        ]
      : []),
    // { type: "divider", key: "divider-4" }
    ...(modules?.showCheck
      ? [
          {
            type: "collapse",
            name: "Check",
            key: "check",
            route: "/check",
            icon: <CheckIcon />,
            noCollapse: true,
            component: <Dashboard />,
          },
        ]
      : []),
    // { type: "divider", key: "divider-6" }
    // Ask PAGER page route (opened by the floating bottom-left widget); no sidebar menu item.
    {
      type: "route",
      key: "riskAdvisor",
      route: "/anticipate/risk-advisor",
      component: <RiskAdvisor />,
    },
    {
      type: "collapse",
      name: "Profile",
      key: "profile",
      icon: <Icon fontSize="small">dashboard</Icon>,
      collapse: [
        {
          name: "Setting",
          key: "setting",
          route: "/dashboard/Profile",
          icon: <Icon fontSize="small">settings</Icon>,
          component: <Profile />,
        },
        {
          name: "Logout",
          key: "logout",
          route: "/authentication/logout",
          icon: <Icon fontSize="small">logout</Icon>,
          component: <Logout />,
        },
      ],
    },
  ];
}

export default getRoutes;
